import React, { memo, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import ReactPlayer from 'react-player';
import {
  FaPlayCircle,
  FaPauseCircle,
  FaVolumeUp,
  FaVolumeMute,
} from 'react-icons/fa';
import { useUiComponentsInternationalContext } from '@repo/static-translations';

const ReactPlayerWrapper = styled.div`
  position: relative;
  padding-top: 56.25%;

  .react-player {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    object-fit: cover;

    .react-player__play-icon {
      z-index: 2;
    }
  }
`;

const CustomControlsWrapper = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 1;

  .mute-button-container {
    position: absolute;
    right: 0.5rem;
    top: 0.5rem;
    z-index: 1;
    display: flex;
    align-items: center;
    height: 30px;

    button {
      background: none;
      border: none;
    }
  }

  .mute-button {
    position: relative;
    z-index: 1;
  }

  .progress-slider {
    position: absolute;
    left: 2.5rem;
    right: 2.5rem;
    bottom: 1rem;
    cursor: pointer;
    accent-color: ${({ theme }) => theme.colors.brandPrimary};
  }

  .play-icon {
    position: absolute;
    bottom: 0.5rem;
    left: 0.5rem;
  }

  svg {
    stroke-width: 3px;
    stroke: ${({ theme }) => theme.colors.brandPrimary};
  }

  &:hover {
    color: ${({ theme }) => theme.colors.carouselBullet};
  }
`;

function Video({ url, index, poster, activateFocus }) {
  const videoRef = useRef<ReactPlayer>(null);

  const { tt } = useUiComponentsInternationalContext();

  const [isBrowser, setIsBrowser] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [played, setPlayed] = useState(0);
  const [seeking, setSeeking] = useState(false);
  const [muted, setMuted] = useState(false);
  const [videoControls, setVideoControls] = useState(false);

  const loop = false;

  const handleReady = () => {
    // Disable tabIndex for iFrame elements (YouTube)
    const iframe = document.querySelector(`#slide-${index} iframe`);
    if (iframe) {
      iframe.setAttribute('tabIndex', '-1');
    }

    setMuted(true);
    setPlaying(true);
  };

  const handlePlay = () => {
    setPlaying(true);
  };

  const handlePause = () => {
    setPlaying(false);
  };

  const handleEnded = () => {
    setPlaying(loop);
  };

  const handleSeekMouseDown = () => {
    setSeeking(true);
  };

  const handleSeekChange = e => {
    setPlayed(parseFloat(e.target.value));
  };

  const handleSeekMouseUp = e => {
    setSeeking(false);
    if (videoRef.current) {
      videoRef.current.seekTo(parseFloat(e.target.value));
    }
  };

  const handleProgress = state => {
    if (!seeking) {
      setPlayed(state.played);
    }
  };

  const handleMuteButtonClick = e => {
    e.stopPropagation();
    setMuted(!muted);
  };

  const onClickPreview = () => {
    setVideoControls(true);
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsBrowser(true);
    }
  }, []);

  return (
    <ReactPlayerWrapper className="react-player-wrapper flex-1 overflow-hidden">
      {videoControls && (
        <CustomControlsWrapper>
          <div className="mute-button-container">
            <button
              type="button"
              className="image-gallery-icon mute-button"
              aria-label={
                muted
                  ? tt(s => s.productCommon.unmuteVideoAriaLabel)
                  : tt(s => s.productCommon.muteVideoAriaLabel)
              }
              onClick={handleMuteButtonClick}
            >
              {muted ? <FaVolumeMute size="25" /> : <FaVolumeUp size="25" />}
            </button>
          </div>

          {playing ? (
            <button
              type="button"
              className="image-gallery-icon play-icon"
              aria-label={tt(s => s.productCommon.pauseVideoAriaLabel)}
              onClick={handlePause}
            >
              <FaPauseCircle size="25" />
            </button>
          ) : (
            <button
              type="button"
              className="image-gallery-icon play-icon"
              aria-label={tt(s => s.productCommon.playVideoAriaLabel)}
              onClick={handlePlay}
            >
              <FaPlayCircle size="25" />
            </button>
          )}
          <input
            className="progress-slider"
            type="range"
            min={0}
            max={0.999999}
            step="any"
            value={played}
            onMouseDown={handleSeekMouseDown}
            onChange={handleSeekChange}
            onMouseUp={handleSeekMouseUp}
          />
        </CustomControlsWrapper>
      )}
      {isBrowser && (
        <ReactPlayer
          tabIndex={activateFocus ? 1 : -1}
          ref={videoRef}
          className="react-player"
          url={url}
          width="100%"
          height="100%"
          controls={false}
          playing={playing}
          volume={1}
          muted={muted}
          loop={loop}
          light={poster}
          playsinline
          onReady={handleReady}
          onPlay={handlePlay}
          onPause={handlePause}
          onEnded={handleEnded}
          onProgress={handleProgress}
          onClickPreview={onClickPreview}
          previewTabIndex={activateFocus ? 0 : -1}
          config={{
            file: {
              attributes: {
                poster,
              },
            },
            youtube: {
              playerVars: { rel: 0 },
            },
          }}
        />
      )}
    </ReactPlayerWrapper>
  );
}

Video.propTypes = {
  url: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]).isRequired,
  index: PropTypes.number.isRequired,
  poster: PropTypes.string.isRequired,
  activateFocus: PropTypes.bool,
};

const MemoizedVideo = memo(Video);

export default MemoizedVideo;
